'use strict';

import React, {Component} from 'react';
import {observer} from "mobx-react";

import {FaExclamationTriangle, FaLink} from "react-icons/fa";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";

import {normalize} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";
import {isEmpty} from "@uw-it-sis/lib-react/lib/Utils";
import HelpPopover from "@uw-it-sis/lib-react/lib/HelpPopover";
import {buildRegistrationSlnList} from '@uw-it-sis/lib-react-myplan/lib/RegistrationUtils';

import scheduleBuilderPlanStore from './ScheduleBuilderPlanStore';
import {ErrorCodes} from "./ScheduleBuilderConstants";
import {coursePlanItemSort} from "./ScheduleBuilderUtils";
import PlanViewStore from "../plan/PlanViewStore";

class ScheduleBuilderPlan extends Component {
    constructor(props) {
        super(props);
        this.props = props;

        this.store = scheduleBuilderPlanStore;

        this.onChange = this.onChange.bind(this);
    }

    onChange(courseCd, e, activityData) {
        this.store.updateCourseInclusionOptions(courseCd, e.target.checked, activityData);
    }

    render() {
        let plannedItemsDisplay;
        let notScheduledMsg;
        let showDupEnrollLegend = false;
        const dupEnrollIndicator = <sup>&dagger;</sup>;
        let planItemsNotScheduled = this.store.getNotScheduledItems();
        if (planItemsNotScheduled.length > 0) {
            notScheduledMsg =
                <p>{planItemsNotScheduled.join(', ')} {planItemsNotScheduled.length > 1 ? "are":"is"} not offered {this.store.getTermAbbr()} quarter, so {planItemsNotScheduled.length > 1 ? "they're":"it's"} excluded in schedule options.</p>
        }

        let planData = PlanViewStore.setClusterStatus(this.store.getPlanData());

        // Get a list of SLNs from the plan data (minus any block reg children)
        let slnKeys = buildRegistrationSlnList(planData);


        // List of block reg types where a planned course has multiple planned block reg parent sections.
        // If a course has multiple parents planned, the type will be added to this list, where later this
        // list will be altered to only have the unique types, so possible end result can be only 1 of 4
        // possibilities; [], ['FIG'], ['Cluster'], or ['FIG, 'Cluster'] for the purpose of generating
        // a message to indicate multiple block reg types have been planned and user should fix.
        let multiPlannedBlockReg = [];

        if (planData.length > 0) {
            let planDataList = planData.sort(coursePlanItemSort).map((item, itemIndex) => {
                let courseCd = normalize(item.courseDetails.code);
                let isCourseIncluded = this.store.getCourseInclusionOption(courseCd);

                // For just this course, build a list of block reg parent types, if multiple the
                // type will be added to multiPlannedBlockReg to indicate messaging is needed display.
                // Code assumes course only contains either FIG blocks or Cluster blocks, and not a combo.
                let blockRegParentTypes = [];
                let multiPlannedBlockRegCourseType = "";

                let sections;
                if (item.planActivities.length > 0) {
                    // Look at all primaries and if dup enroll is true on all, then set flag to true
                    let hasDupEnrollPrimaries = item.planActivities.filter(activity => activity.primary).every(activity => activity.duplicateEnrollmentAllowed) && item.planActivities.filter(activity => activity.primary).length > 1;
                    if (!showDupEnrollLegend && hasDupEnrollPrimaries) {
                        showDupEnrollLegend = hasDupEnrollPrimaries;
                    }

                    let sectionsList = item.planActivities.map((activity, activityIndex) => {
                        let isSectionIncluded = this.store.getCourseInclusionOption(courseCd, activity.code);

                        let blockReg;
                        if (activity.plannedBlockRegistration) {
                            let blockRegType = (activity.blockRegistration.type.trim() === "FIG" ? "FIG" : "Cluster")
                            let blockCoursesTitle = (blockRegType === "FIG" ? "FIG " : "") + "Cluster Registration Section";
                            let blockCoursesList = [...activity.plannedBlockRegistrationData];
                            blockCoursesList.sort(function(a, b) {
                                // sort block course codes so that the first one is the current course
                                let currentCourse = normalize(activity.courseCode);
                                if (a.includes(currentCourse)) return -1;
                                if (b.includes(currentCourse)) return 1;
                                return a - b
                            });
                            let blockCoursesListEnd = blockCoursesList.pop();

                            // A list of block parent section codes
                            let blockParentCodes = [];
                            // Iterate over the block courses and sections, and build a list of parent section codes
                            // if the SLN is within the list of parent SLNs and in the list of SLNs that are planned.
                            activity.blockRegistration.courses.map( course => {
                                course.sections.map( section => {
                                    if ( activity.blockRegistration.parentSlns.includes(section.sln) && slnKeys.includes(section.sln) ) {
                                        blockParentCodes.push(section.code);
                                    }
                                });
                            });

                            // section is a block parent, add it's type to blockRegParentTypes
                            if (activity.blockRegistration.parent) {
                                blockRegParentTypes.push(activity.blockRegistration.type);
                            }

                            // Only show the block reg parent code if its singular, if there are multiple,
                            // then section is a child of multiple block reg
                            blockReg =
                                <HelpPopover title={blockCoursesTitle} icon={FaLink} placement="top" className="text-muted ms-2" flip="horizontal">
                                    {`${blockCoursesList.join(", ")} and ${blockCoursesListEnd} are part of a cluster${blockParentCodes.length === 1 ? ', ' + blockRegType + ' ' + blockParentCodes.join() : ''}. All cluster sections will be sent to Registration together.`}
                                </HelpPopover>;
                        }

                        let activityIssue;
                        if ( ! isEmpty(activity.validationIssues) ) {
                            let activityIssueList = [];
                            for (const errorCode in activity.validationIssues) {
                                if (ErrorCodes.hasOwnProperty(errorCode)) {
                                    activity.validationIssues[errorCode].map(error => {
                                        activityIssueList.push(error);
                                    });
                                }
                            }
                            if (activityIssueList.length > 0) {
                                activityIssueList.push("Thus, the Schedule Builder didn't include the planned section for this course to generate schedule options.");
                                activityIssue =
                                    <div className="ms-auto">
                                        <HelpPopover icon={FaExclamationTriangle}
                                                     title={`${courseCd} ${activity.code} Issues`}
                                                     placement="top"
                                                     className="text-warning">
                                            {activityIssueList.join(" ")}
                                        </HelpPopover>
                                    </div>;
                            }
                        }

                        let activityTypeBadge =
                            <Badge bg="light-gray" className="text-dark fs-10 text-uppercase fw-normal border ms-2">
                                {activity.activityOfferingType}
                            </Badge>; //

                        let summerBadge;
                        if (activity.hasOwnProperty("summerTerm") && activity.summerTerm) {
                            summerBadge =
                                <Badge bg="light-gray" className="text-dark fs-10 text-uppercase fw-normal border ms-2">
                                    {activity.summerTerm}
                                </Badge>;
                        }

                        let activityDataObject = {
                            "activityCd": activity.code,
                            "primary": activity.primary,
                            "primaryActivityCd": activity.primaryActivityOfferingCode
                        }

                        return (
                            <li key={activityIndex} className="d-flex align-items-start">
                                <Form.Check id={`plan-item-${activity.planItemId}`}
                                            type="checkbox"
                                            className="me-0"
                                            style={{lineHeight: '1.666rem'}}
                                            inline>
                                    <Form.Check.Input type="checkbox"
                                                      name={`${courseCd} ${activity.code}`}
                                                      checked={isSectionIncluded}
                                                      disabled={this.store.getCourseInclusionOption(courseCd, activity.code, "disabled")}
                                                      onChange={(e) => this.onChange(courseCd, e, activityDataObject)} />
                                    <Form.Check.Label>{activity.code}{activity.primary && hasDupEnrollPrimaries ? dupEnrollIndicator : null}</Form.Check.Label>
                                </Form.Check>
                                <div className="d-flex-inline">
                                    {activityTypeBadge}
                                    {blockReg}
                                    {summerBadge}
                                </div>
                                {activityIssue}
                            </li>
                        )
                    });

                    // If multiple block reg parents exist for this course, then the type of block is added to multiPlannedBlockReg
                    // to indicate an issue to user and display messaging of what type of block has multiple parents planned.
                    if (blockRegParentTypes.length > 1) {
                        // Assumes blockRegParentTypes can only have one type, just either 0, 1, or many
                        // Since type is determined by course code, should never have a combo of types
                        multiPlannedBlockRegCourseType = blockRegParentTypes[0];
                        if (multiPlannedBlockRegCourseType !== "FIG") {
                            // If not a FIG, type name is Cluster
                            multiPlannedBlockRegCourseType = "Cluster";
                        }
                        multiPlannedBlockReg.push(multiPlannedBlockRegCourseType);
                    }

                    sections = <ul className="list-unstyled mb-0">{sectionsList}</ul>;
                }

                let courseIssue;
                if ( ! isEmpty(item.validationIssues) ) {
                    let courseIssueList = [];
                    for (const errorCode in item.validationIssues) {
                        if (ErrorCodes.hasOwnProperty(errorCode)) {
                            // Special case for block reg when too many block parents have been planned within course
                            if (errorCode === "REMOVE_ALL_BUT_ONE_SECTION" && multiPlannedBlockRegCourseType !== "") {
                                courseIssueList.push(`You have too many ${multiPlannedBlockRegCourseType} sections in your plan.`)
                            } else {
                                item.validationIssues[errorCode].map(error => {
                                    courseIssueList.push(error);
                                });
                            }
                        }
                    }

                    if (courseIssueList.length > 0) {
                        if (multiPlannedBlockRegCourseType) {
                            courseIssueList.push(`Schedule Builder didn't include them when generating schedule options. Select just one ${multiPlannedBlockRegCourseType} section and click the "Update Schedule Options" button.`);
                        } else {
                            courseIssueList.push("Schedule Builder didn't include the planned section(s) when generating schedule options.");
                        }
                        courseIssue =
                            <div className="ms-auto">
                                <HelpPopover icon={FaExclamationTriangle}
                                             title={`${courseCd} Issues`}
                                             placement="top"
                                             className="text-warning">
                                    {courseIssueList.join(" ")}
                                </HelpPopover>
                            </div>;
                    }
                }

                return (
                    <Col md={3} sm={4} xs={12} key={itemIndex}>
                        <div className="border m-1 p-2">
                            <div className="d-flex align-items-center">
                                <Form.Check id={`plan-item-${item.planItemDataObject.id}`}
                                            name={courseCd}
                                            type="checkbox"
                                            className="me-0"
                                            checked={isCourseIncluded}
                                            disabled={this.store.getCourseInclusionOption(courseCd, null, "disabled")}
                                            label={courseCd}
                                            inline
                                            onChange={(e) => this.onChange(courseCd, e)} />
                                {courseIssue}
                            </div>
                            {sections}
                        </div>
                    </Col>
                );
            });

            let dupEnrollLegend;
            if (showDupEnrollLegend) {
                dupEnrollLegend = <small className="d-block px-2 mt-2 mb-0">{dupEnrollIndicator} These are primary sections that can be registered concurrently.</small>;
            }

            let multiPlannedBlockRegMsg;
            // Make list unique list of types, no duplicates
            multiPlannedBlockReg = [...new Set(multiPlannedBlockReg)];
            if (multiPlannedBlockReg.length > 0) {
                multiPlannedBlockRegMsg =
                    <div className="d-flex align-items-start">
                        <FaExclamationTriangle className="text-warning me-2 mt-1" aria-hidden={true} />
                        <p>You have too many {multiPlannedBlockReg.join('/')} sections in your plan. Select just one {multiPlannedBlockReg.join('/')} section and click the "Update Schedule Options" button.</p>
                    </div>
            }

            plannedItemsDisplay =
                <>
                    <p>The following courses are used to generate your schedule options. You can explore different schedule options by deselecting courses and sections.</p>
                    {notScheduledMsg}
                    {multiPlannedBlockRegMsg}
                    <Row className="g-0">
                        {planDataList}
                    </Row>
                    {dupEnrollLegend}
                </>

        } else {
            plannedItemsDisplay =
                <div className="py-3">
                    <p><strong>No scheduled courses in your plan.</strong> Add a course and explore schedule options.
                        Want to explore courses and curriculums? <a href="/course">Find courses</a> of interest to you.</p>
                    {notScheduledMsg}
                </div>
        }

        return (
            <>
                <h3 className="h4">Planned Courses</h3>
                {plannedItemsDisplay}
            </>
        );
    }
}

export default observer(ScheduleBuilderPlan);